import { Component } from '@theme/component';
import { applySizeGuideUnit, prepareSizeGuideTables } from '@theme/bt-size-guide-tables';

/**
 * Size guide page — metafield tables and cm/inches tabs (no horizontal scroll;
 * table fits viewport like live-theme).
 *
 * @extends {Component}
 */
class BtSizeGuidePage extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#prepareTables();
    customElements.whenDefined('rte-formatter').then(() => {
      if (!this.isConnected) return;
      requestAnimationFrame(() => this.#prepareTables());
    });

    this.querySelectorAll('[data-bt-sg-tab]').forEach((tab) => {
      tab.addEventListener('click', this.#onTabClick, { signal });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #body() {
    const body = this.querySelector('[data-bt-sg-body]');
    return body instanceof HTMLElement ? body : null;
  }

  #prepareTables() {
    const body = this.#body();
    if (!body) return;

    prepareSizeGuideTables(body);

    const tabs = this.querySelector('[data-bt-sg-tabs]');
    if (tabs instanceof HTMLElement) tabs.hidden = false;

    if (!this.dataset.unit) this.dataset.unit = 'cm';
  }

  /**
   * @param {Event} event
   */
  #onTabClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    const unit = tab.dataset.btSgTab;
    if (unit !== 'cm' && unit !== 'in') return;
    this.#applyUnit(unit);
  };

  /**
   * @param {'cm' | 'in'} unit
   */
  #applyUnit(unit) {
    this.dataset.unit = unit;

    this.querySelectorAll('[data-bt-sg-tab]').forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const selected = tab.dataset.btSgTab === unit;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.classList.toggle('is-active', selected);
    });

    const body = this.#body();
    if (body) applySizeGuideUnit(body, unit);
  }
}

if (!customElements.get('bt-size-guide-page')) {
  customElements.define('bt-size-guide-page', BtSizeGuidePage);
}
